import React, { useState } from "react";
import {
  Dialog,
  Flex,
  Text,
  Label,
  CloseIcon,
  Loader,
} from "@fluentui/react-northstar";
import {
  Card,
  Progress,
  List,
  Typography,
  Row,
  Col,
  Divider,
  Tooltip,
} from "antd";
import { LightbulbCircleOutlined } from "@mui/icons-material";
import { CheckmarkCircleIcon, InfoIcon } from "@fluentui/react-northstar";
import "./QuickViewDialog.scss";
import { CommentsList } from "../SingleAssignment/components/common/CommentsList";
import { AttachmentButton } from "../SingleAssignment/components/common/AttachmentButton";
import { RenderAttachmentContent } from "../SingleAssignment/components/common/RenderAttachmentContent";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const getIconForHighlight = (type) => {
  switch (type) {
    case "strength":
      return <CheckmarkCircleIcon style={{ color: "#52c41a" }} />;
    case "insight":
      return <LightbulbCircleOutlined style={{ color: "#1890ff" }} />;
    case "suggestion":
      return <InfoIcon style={{ color: "#faad14" }} />;
    default:
      return <InfoIcon style={{ color: "#faad14" }} />;
  }
};

const ResultBox = ({ children }) => (
  <div
    style={{
      background: "#fafbfc",
      border: "1px solid #e6e6e6",
      borderRadius: "8px",
      padding: "12px",
      height: "100%",
    }}
  >
    {children}
  </div>
);

const QuickViewDialog = ({
  open,
  onClose,
  student,
  loading,
  attachments = [],
}) => {
  if (!student) return null;
  const { t } = useTranslation();

  const translation = t("assignment").summary.batch;
  const [selectedResponseType, setSelectedResponseType] = useState("text");

  const {
    name,
    submissionDate,
    grade,
    confidence,
    timeTaken,
    rubrics = [],
    gradingResult,
    overallScore,
  } = student;

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes} : ${seconds}`;
  };

  return (
    <Dialog
      open={open}
      onCancel={onClose}
      header={`${translation.quickView.title} - ${name}`}
      className="quick-view-dialog"
      content={
        loading ? (
          <Flex hAlign="center" vAlign="center" style={{ height: "100%" }}>
            <Loader />
          </Flex>
        ) : (
          <div style={{ padding: "0 16px" }}>
            {/* Basic Info */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <ResultBox>
                  <Text type="secondary">
                    {translation.quickView.assignedGrade}
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {grade}
                  </Title>
                </ResultBox>
              </Col>
              <Col span={6}>
                <ResultBox>
                  <Text type="secondary">
                    {translation.quickView.aiConfidenceScore}
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {confidence}%
                  </Title>
                </ResultBox>
              </Col>
              <Col span={6}>
                <ResultBox>
                  <Text type="secondary">
                    {translation.quickView.totalScore}
                  </Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {overallScore.score} / {overallScore.max}
                  </Title>
                </ResultBox>
              </Col>
              {!gradingResult?.overWrite && (
                <Col span={6}>
                  <ResultBox>
                    <Text type="secondary">
                      {translation.quickView.aiProcessingTime}
                    </Text>
                    <Title level={4} style={{ margin: 0 }}>
                      {formatTime(timeTaken)}
                    </Title>
                  </ResultBox>
                </Col>
              )}
            </Row>

            {/* AI Analysis Highlights */}
            <Card
              title={translation.quickView.aiAnalysisSummary}
              style={{ marginBottom: 16 }}
            >
              <List
                dataSource={gradingResult?.highlights || []}
                renderItem={(item) => (
                  <List.Item>
                    <Flex vAlign="center" gap="gap.small">
                      {getIconForHighlight(item.type)}
                      <Text>{item.text}</Text>
                    </Flex>
                  </List.Item>
                )}
              />
            </Card>

            {/* Rubric Evaluation */}
            <Card title={translation.quickView.detailedRubricAssessment}>
              <List
                dataSource={rubrics}
                renderItem={(item) => (
                  <List.Item>
                    <div style={{ width: "100%" }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text strong>
                            {item.label} ({translation.quickView.maximum}:{" "}
                            {item.max} {translation.quickView.points})
                          </Text>
                        </Col>
                        <Col>
                          <Text>
                            {item.score}/{item.max}
                          </Text>
                        </Col>
                      </Row>
                      <Progress
                        percent={Math.round((item.score / item.max) * 100)}
                        showInfo={false}
                        strokeColor={item.color}
                        style={{ margin: "4px 0" }}
                      />
                      <Row justify="space-between" align="middle">
                        <Col>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {translation.quickView.aiConfidence}:{" "}
                            {item.confidence}%
                          </Text>
                        </Col>
                        <Col>
                          <Tooltip title={item.comment}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.comment}
                            </Text>
                          </Tooltip>
                        </Col>
                      </Row>
                    </div>
                  </List.Item>
                )}
              />
            </Card>

            {/* Overall Feedback */}
            <Card
              title={translation.quickView.aiGeneratedStudentFeedback}
              style={{ marginTop: 16 }}
            >
              <div
                style={{
                  background: "#fafbfc",
                  border: "1px solid #e6e6e6",
                  borderRadius: "8px",
                  padding: "12px",
                  whiteSpace: "pre-wrap",
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    gradingResult?.overallFeedback ||
                    translation.quickView.noFeedbackAvailable,
                }}
              />
            </Card>
            {/* Attachments with annotations */}
            {attachments && attachments.length > 0 && (
              <Card
                title={translation.quickView.instructorComments}
                style={{ marginTop: 16 }}
              >
                <Row gutter={[5, 5]}>
                  {attachments.map((attachment, index) => (
                    <Col key={`attachment-${index + 1}`}>
                      <AttachmentButton
                        icon={attachment.icon}
                        content={attachment.content}
                        isActive={attachment.type === selectedResponseType}
                        onClick={() => setSelectedResponseType(attachment.type)}
                      />
                    </Col>
                  ))}
                </Row>
                <Divider />
                <RenderAttachmentContent
                  attachment={attachments.find(
                    (attachment) => attachment.type === selectedResponseType
                  )}
                  isAnnotationMode={false}
                  gradingResult={gradingResult}
                />
              </Card>
            )}
            <Card
              title={translation.quickView.additionalComments}
              style={{ marginTop: 16 }}
            >
              <CommentsList comments={gradingResult?.comments} />
            </Card>
          </div>
        )
      }
      headerAction={{
        icon: <CloseIcon />,
        title: translation.quickView.closeDialog,
        onClick: () => {
          onClose();
        },
      }}
    />
  );
};

export default QuickViewDialog;
